import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { adminApi, ApiError, clientApi, clientErrorMessage } from '../lib/api';
import { getStoredClientName, setStoredClientName } from '../lib/clientName';
import { avatarTone, formatNoteTime, initialOf, sameName } from '../lib/noteDisplay';
import { sortNoteEntries, LEGACY_NOTES_TITLE, MAX_NOTE_BODY_CHARS, MAX_NOTE_AUTHOR_CHARS } from '../../shared/clientNotes';
import type { NoteEntry, PublicNoteEntry } from '../../shared/clientNotes';
import { ulid } from '../../shared/ulid';
import Eyebrow from './Eyebrow';
import Button from './Button';
import { showToast } from './Toaster';
import type { ReviewKind } from '../../shared/progress';

const POLL_MS = 30_000;

interface ClientProps {
  mode: 'client';
  slug: string;
  onPendingChange?: (slug: string, pending: boolean) => void;
  /** The noun in the helper copy: "script" unless this is an interview brief. */
  kind?: ReviewKind;
  /** Drop the "highlight it and comment" advice when comments are off. */
  commentsEnabled?: boolean;
}

interface AdminProps {
  mode: 'admin';
  scriptId: string;
}

type Props = ClientProps | AdminProps;

/**
 * General notes (C10): named, dated entries instead of one shared text box,
 * so a team of three can each leave thoughts without writing over each
 * other. Workflow only; never in the printed/PDF output (data-print-hide).
 *
 * Client mode: a composer plus the list, newest first. Notes written on this
 * device can be edited and removed. Admin mode: Henry reads every note and
 * can delete any.
 */
export interface ClientNotesHandle {
  /** Scroll the panel into view and focus the composer. Called by the
   *  "General notes" action in the review card. */
  open: () => void;
  /** Before Send for review: add any typed but un-added note first, using
   *  `authorName` when no name is remembered yet. False means retry. */
  flush: (authorName?: string) => Promise<boolean>;
}

const ClientNotesPanel = forwardRef<ClientNotesHandle, Props>(function ClientNotesPanel(
  props,
  ref,
) {
  if (props.mode === 'client') return <ClientNotes {...props} ref={ref} />;
  return <AdminNotes {...props} />;
});

export default ClientNotesPanel;

// ---------- Shared bits ----------

const LINK_BTN =
  'font-mono text-[10px] uppercase tracking-[0.12em] font-medium text-muted hover:text-fg transition-colors ' +
  'focus:outline-none focus-visible:shadow-focus rounded-sm';

function draftKey(slug: string) {
  return `hsp_note_draft_${slug}`;
}

interface NoteDraft {
  text: string;
  pending: { id: string; body: string; author_name: string } | null;
}

function readDraft(slug: string, fallback: NoteDraft = { text: '', pending: null }): NoteDraft {
  try {
    const raw = localStorage.getItem(draftKey(slug)) ?? '';
    try {
      const parsed = JSON.parse(raw) as NoteDraft;
      if (parsed && typeof parsed.text === 'string') {
        const p = parsed.pending;
        return { text: parsed.text, pending: p && typeof p.id === 'string' &&
          typeof p.body === 'string' && typeof p.author_name === 'string' ? p : null };
      }
    } catch {}
    // Preserve plain-text drafts left by the first version of this panel.
    return { text: raw, pending: null };
  } catch {
    return fallback;
  }
}

function writeDraft(slug: string, draft: NoteDraft) {
  try {
    if (draft.text || draft.pending) localStorage.setItem(draftKey(slug), JSON.stringify(draft));
    else localStorage.removeItem(draftKey(slug));
  } catch {}
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function Avatar({ name, legacy, large }: { name: string; legacy?: boolean; large?: boolean }) {
  const size = large
    ? 'w-6 h-6 text-[12px] md:w-[30px] md:h-[30px] md:text-[14px]'
    : 'w-6 h-6 text-[12px]';
  if (legacy) {
    return (
      <span
        aria-hidden
        className={`${size} shrink-0 rounded-full inline-flex items-center justify-center bg-line-soft text-faint border border-dashed border-faint/60 font-display font-semibold leading-none`}
      >
        ≡
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className={`${size} shrink-0 rounded-full inline-flex items-center justify-center ${avatarTone(name)} text-accent-fg font-display font-semibold leading-none`}
    >
      {initialOf(name)}
    </span>
  );
}

/** A textarea that grows with its text. */
function GrowingTextarea(
  props: React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    inputRef?: React.Ref<HTMLTextAreaElement>;
    minRows?: number;
  },
) {
  const { inputRef, minRows = 3, className = '', value, ...rest } = props;
  const local = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = local.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      {...rest}
      value={value}
      rows={minRows}
      ref={(el) => {
        local.current = el;
        if (typeof inputRef === 'function') inputRef(el);
        else if (inputRef) (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
      }}
      className={`block w-full resize-none overflow-hidden bg-transparent text-fg placeholder:text-faint focus:outline-none ${className}`}
    />
  );
}

/** On phones a long note clamps at 8 lines with "Show all", so one essay
 *  doesn't bury the rest. */
function NoteBody({ text, compact }: { text: string; compact?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef<HTMLParagraphElement | null>(null);

  useLayoutEffect(() => {
    if (expanded) return;
    const measure = () => {
      const el = ref.current;
      if (el) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [text, expanded]);

  return (
    <>
      <p
        ref={ref}
        className={`whitespace-pre-wrap break-words text-muted ${
          compact ? 'text-[13.5px] leading-[1.6] mt-1' : 'text-[14px] leading-[1.6] mt-1.5'
        } ${expanded ? '' : 'max-md:line-clamp-8'}`}
      >
        {text}
      </p>
      {overflowing && !expanded && (
        <button type="button" onClick={() => setExpanded(true)} className={`${LINK_BTN} mt-1.5 text-accent hover:text-accent-hover`}>
          Show all
        </button>
      )}
    </>
  );
}

// ---------- Client ----------

type AddState = { kind: 'idle' } | { kind: 'saving' } | { kind: 'error'; message: string };

const ClientNotes = forwardRef<ClientNotesHandle, ClientProps>(function ClientNotes(
  { slug, kind = 'script', onPendingChange, commentsEnabled = true },
  ref,
) {
  const [notes, setNotes] = useState<PublicNoteEntry[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [storedName, setStoredName] = useState(() => getStoredClientName().trim());
  const [changingName, setChangingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [draft, setDraft] = useState(() => readDraft(slug));
  const draftRef = useRef(draft);
  const text = draft.text;
  function updateDraft(next: NoteDraft) {
    draftRef.current = next;
    writeDraft(slug, next);
    setDraft(next);
  }
  const [addState, setAddState] = useState<AddState>({ kind: 'idle' });
  const [highlight, setHighlight] = useState<{ id: string; on: boolean } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const loadRequestRef = useRef(0);
  const inFlightRef = useRef<Promise<boolean> | null>(null);
  const onPendingChangeRef = useRef(onPendingChange);
  onPendingChangeRef.current = onPendingChange;

  const showNameField = !storedName || changingName;
  const nameForAdd = (showNameField ? nameDraft : storedName).trim();
  const noun = kind === 'brief' ? 'brief' : 'script';

  const load = useCallback(async () => {
    const request = ++loadRequestRef.current;
    try {
      const res = await clientApi.listNotes(slug);
      if (request !== loadRequestRef.current) return;
      setNotes(res.notes);
      setLoadFailed(false);
    } catch {
      if (request !== loadRequestRef.current) return;
      setLoadFailed(true);
    }
  }, [slug]);

  // Load, then keep up with teammates: a light poll while the tab is
  // visible, and a refresh whenever it comes back into view.
  useEffect(() => {
    void load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      ++loadRequestRef.current;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  // The soft teal ring on a just-added note fades over 1.5s.
  useEffect(() => {
    if (!highlight?.on) return;
    const t = window.setTimeout(() => setHighlight((h) => (h ? { ...h, on: false } : h)), 60);
    return () => window.clearTimeout(t);
  }, [highlight]);

  const add = useCallback(
    async (fallbackName?: string): Promise<boolean> => {
      const body = draftRef.current.pending?.body ?? draftRef.current.text.trim();
      const name = draftRef.current.pending?.author_name ?? (nameForAdd || fallbackName?.trim() || '');
      if (!body) return true;
      if (!name) {
        setAddState({ kind: 'error', message: 'Add your name so Henry knows who wrote it.' });
        return false;
      }
      if (inFlightRef.current) return inFlightRef.current;
      const submission = draftRef.current.pending ?? { id: ulid(), author_name: name, body };
      const { id } = submission;
      updateDraft({ ...draftRef.current, pending: submission });
      setAddState({ kind: 'saving' });
      onPendingChangeRef.current?.(slug, true);
      const run = (async () => {
        try {
          const { note } = await clientApi.addNote(slug, { id, author_name: name, body });
          // Reconcile the composer and storage independently. Storage writes can
          // fail, and this request may have outlived a panel remount.
          const acknowledge = (current: NoteDraft): NoteDraft => ({
            text: current.pending?.id === id && current.text.trim() === body ? '' : current.text,
            pending: current.pending?.id === id ? null : current.pending,
          });
          const latestStored = readDraft(slug, draftRef.current);
          const next = acknowledge(draftRef.current);
          draftRef.current = next;
          setDraft(next);
          writeDraft(slug, acknowledge(latestStored));
          ++loadRequestRef.current;
          setStoredClientName(note.author_name);
          setStoredName(note.author_name);
          setChangingName(false);
          setNotes((prev) => sortNoteEntries([note, ...(prev ?? []).filter((n) => n.id !== note.id)]));
          if (!prefersReducedMotion()) setHighlight({ id: note.id, on: true });
          setAddState({ kind: 'idle' });
          return true;
        } catch (err) {
          setAddState({ kind: 'error', message: clientErrorMessage(err, "That didn't save. Try again.") });
          return false;
        } finally {
          inFlightRef.current = null;
          onPendingChangeRef.current?.(slug, false);
        }
      })();
      inFlightRef.current = run;
      return run;
    },
    [slug, nameForAdd],
  );

  useImperativeHandle(
    ref,
    () => ({
      flush: async (authorName?: string) => {
        if (!(await add(authorName))) return false;
        // A restored pending request can coexist with newer composer text.
        if (draftRef.current.text.trim()) return add(authorName);
        return true;
      },
      open() {
        requestAnimationFrame(() => {
          document.getElementById('general-notes')?.scrollIntoView({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'center',
          });
          (showNameField && !nameDraft.trim() ? nameRef.current : textareaRef.current)?.focus();
        });
      },
    }),
    [add, showNameField, nameDraft],
  );

  function onTextChange(next: string) {
    updateDraft({ ...draftRef.current, text: next });
    if (addState.kind === 'error') setAddState({ kind: 'idle' });
  }

  function startChangeName() {
    setNameDraft('');
    setChangingName(true);
    requestAnimationFrame(() => nameRef.current?.focus());
  }

  const canAdd = (!!draft.pending || (!!nameForAdd && !!text.trim())) && addState.kind !== 'saving';
  const count = notes?.length ?? 0;
  const anyMine = notes?.some((n) => n.mine) ?? false;

  return (
    <section
      id="general-notes"
      data-print-hide
      className="mt-12 bg-elevated border border-line rounded-md p-4 md:p-6"
    >
      <header className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <Eyebrow accent>// General notes</Eyebrow>
          <p className="text-faint text-[12px] mt-1.5">
            Big-picture thoughts for Henry. Everyone reviewing this {noun} can read them.
          </p>
        </div>
        {count > 0 && (
          <span className="shrink-0 pt-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-faint whitespace-nowrap">
            {count}
            <span className="max-sm:hidden"> {count === 1 ? 'note' : 'notes'}</span>
          </span>
        )}
      </header>

      <div
        className="
          bg-card border border-line rounded-sm p-3
          transition-[border-color,box-shadow] duration-150
          focus-within:border-accent focus-within:shadow-focus
        "
      >
        {showNameField && (
          <>
            <label htmlFor={`note-name-${slug}`} className="block font-mono text-[10px] uppercase tracking-[0.08em] text-muted mb-1.5">
              Your name
            </label>
            <input
              id={`note-name-${slug}`}
              ref={nameRef}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              maxLength={MAX_NOTE_AUTHOR_CHARS}
              autoComplete="name"
              placeholder="So Henry knows who wrote it"
              className="
                w-full mb-2.5 px-3 py-2 bg-card border border-line rounded-sm
                text-fg text-[13px] placeholder:text-faint
                focus:border-accent focus:outline-none
              "
            />
          </>
        )}
        <GrowingTextarea
          inputRef={textareaRef}
          aria-label="New general note"
          value={text}
          readOnly={addState.kind === 'saving'}
          maxLength={MAX_NOTE_BODY_CHARS}
          minRows={3}
          onChange={(e) => onTextChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              if (canAdd) void add();
            }
          }}
          placeholder={anyMine ? 'Add another note…' : "Overall thoughts, tone, anything that isn't about one line…"}
          className="min-h-16 text-[14px] leading-[1.55]"
        />
        <div className="flex items-center justify-between gap-2.5 mt-2.5 flex-wrap">
          {showNameField ? (
            <span className="text-[12px] text-faint">Notes can be edited later from this device.</span>
          ) : (
            <span className="inline-flex items-center gap-2 text-[12px] text-faint min-w-0">
              <Avatar name={storedName} />
              <span className="min-w-0">
                Posting as{' '}
                <button
                  type="button"
                  onClick={startChangeName}
                  className="font-semibold text-muted hover:text-fg focus:outline-none focus-visible:shadow-focus rounded-sm"
                  aria-label={`Posting as ${storedName}. Change name`}
                >
                  {storedName}
                </button>
                <span className="max-sm:hidden">
                  {' · '}
                  <button
                    type="button"
                    onClick={startChangeName}
                    className="font-medium text-accent hover:text-accent-hover focus:outline-none focus-visible:shadow-focus rounded-sm"
                  >
                    Not you?
                  </button>
                </span>
              </span>
            </span>
          )}
          <Button
            variant="primary"
            size="sm"
            disabled={!canAdd}
            onClick={() => void add()}
            className="max-sm:min-h-11 max-sm:px-4"
          >
            {addState.kind === 'saving' ? 'Adding…' : 'Add note'}
          </Button>
        </div>
        {addState.kind === 'error' && (
          <p role="alert" className="mt-2 flex items-center gap-3 flex-wrap text-[12.5px] text-error">
            {addState.message}
            {canAdd && (
              <button type="button" onClick={() => void add()} className={`${LINK_BTN} text-accent hover:text-accent-hover`}>
                Retry
              </button>
            )}
          </p>
        )}
      </div>

      {notes === null ? (
        loadFailed ? (
          <p className="mt-4 text-[13px] text-muted">
            Notes couldn't load.{' '}
            <button type="button" onClick={() => void load()} className={`${LINK_BTN} text-accent hover:text-accent-hover`}>
              Retry
            </button>
          </p>
        ) : null
      ) : notes.length === 0 ? (
        <p className="mt-4 border border-dashed border-faint/60 rounded-sm p-[18px] text-center text-[13px] text-muted">
          No notes yet. Use these for thoughts about the whole {noun}.
          {commentsEnabled && ' For a single line, highlight it and comment instead.'}
        </p>
      ) : (
        <ul className="mt-4 flex flex-col gap-2.5">
          {notes.map((note) => (
            <ClientEntry
              key={note.id}
              slug={slug}
              note={note}
              storedName={storedName}
              ringOn={highlight?.id === note.id ? highlight.on : null}
              onChanged={(next) => {
                ++loadRequestRef.current;
                setNotes((prev) => (prev ?? []).map((n) => (n.id === next.id ? next : n)));
              }}
              onRemoved={(id) => {
                ++loadRequestRef.current;
                setNotes((prev) => (prev ?? []).filter((n) => n.id !== id));
              }}
              onPendingChange={(pending) => onPendingChangeRef.current?.(slug, pending)}
            />
          ))}
        </ul>
      )}
    </section>
  );
});

function ClientEntry({
  slug,
  note,
  storedName,
  ringOn,
  onChanged,
  onRemoved,
  onPendingChange,
}: {
  slug: string;
  note: PublicNoteEntry;
  storedName: string;
  /** null = not the just-added note; true/false drive the fading ring. */
  ringOn: boolean | null;
  onChanged: (note: PublicNoteEntry) => void;
  onRemoved: (id: string) => void;
  onPendingChange: (pending: boolean) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editRef = useRef<HTMLTextAreaElement | null>(null);

  const otherDevice = !note.mine && !note.legacy && sameName(note.author_name, storedName);

  function startEdit() {
    setDraft(note.body);
    setError(null);
    setConfirmRemove(false);
    setEditing(true);
    requestAnimationFrame(() => {
      const el = editRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  }

  function cancel() {
    setEditing(false);
    setConfirmRemove(false);
    setError(null);
  }

  async function save() {
    if (!draft.trim()) {
      setError('Write a note first, or remove it.');
      return;
    }
    setBusy(true);
    onPendingChange(true);
    setError(null);
    try {
      const { note: next } = await clientApi.updateNote(slug, note.id, draft);
      onChanged(next);
      setEditing(false);
    } catch (err) {
      setError(clientErrorMessage(err, "That didn't save. Try again."));
    } finally {
      setBusy(false);
      onPendingChange(false);
    }
  }

  async function remove() {
    setBusy(true);
    onPendingChange(true);
    setError(null);
    try {
      await clientApi.deleteNote(slug, note.id);
      onRemoved(note.id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        onRemoved(note.id);
        return;
      }
      setError(clientErrorMessage(err, "That didn't remove. Try again."));
    } finally {
      setBusy(false);
      onPendingChange(false);
    }
  }

  const ring =
    ringOn === null
      ? ''
      : `motion-safe:transition-shadow motion-safe:duration-[1500ms] ${ringOn ? 'shadow-[0_0_0_3px_var(--color-accent-glow-soft)]' : ''}`;

  return (
    <li
      className={`
        flex gap-2.5 md:gap-3 rounded-sm p-3 md:px-4 md:py-3.5 border
        ${note.legacy ? 'bg-transparent border-dashed border-line' : 'bg-card'}
        ${!note.legacy && note.mine ? 'border-accent/35' : !note.legacy ? 'border-line' : ''}
        ${editing ? '!border-accent shadow-[0_0_0_3px_var(--color-accent-glow-soft)]' : ''}
        ${busy && !editing ? 'opacity-50' : ''}
        ${ring}
      `}
    >
      <Avatar name={note.author_name} legacy={note.legacy} large />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-x-2 gap-y-0.5 flex-wrap">
          <b className="text-[13px] font-semibold text-fg">{note.legacy ? LEGACY_NOTES_TITLE : note.author_name}</b>
          {note.mine && (
            <span className="font-mono text-[9px] uppercase tracking-[0.12em] font-semibold text-accent bg-accent/10 rounded-full px-[7px] py-px">
              You
            </span>
          )}
          {note.legacy ? (
            <span className="font-mono text-[10px] tracking-[0.02em] text-faint max-sm:basis-full">From before notes had names</span>
          ) : (
            <time
              dateTime={new Date(note.created_at).toISOString()}
              className="font-mono text-[10px] tracking-[0.02em] text-faint max-sm:order-last max-sm:basis-full"
            >
              {formatNoteTime(note.created_at)}
            </time>
          )}
          {note.edited && <span className="font-mono text-[10px] tracking-[0.02em] text-faint">· edited</span>}
          {note.mine && !editing && (
            <span className="ml-auto">
              <button type="button" onClick={startEdit} className={LINK_BTN}>
                Edit
              </button>
            </span>
          )}
        </div>

        {editing ? (
          <>
            <div className="mt-2 bg-card border border-accent rounded-sm px-3 py-2 shadow-focus">
              <GrowingTextarea
                inputRef={editRef}
                aria-label="Edit your note"
                value={draft}
                maxLength={MAX_NOTE_BODY_CHARS}
                minRows={2}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    cancel();
                  }
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    void save();
                  }
                }}
                className="text-[14px] leading-[1.55]"
              />
            </div>
            {confirmRemove ? (
              <div className="mt-2.5 flex items-center justify-between gap-2.5 flex-wrap rounded-sm px-3 py-2.5 bg-error/10 border border-error/25 text-[12.5px] text-fg">
                <span>Remove this note?</span>
                <span className="flex gap-1.5">
                  <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(false)} disabled={busy}>
                    Keep
                  </Button>
                  <button
                    type="button"
                    onClick={() => void remove()}
                    disabled={busy}
                    className="inline-flex items-center rounded-full px-3 py-1.5 text-[13px] font-semibold bg-error text-accent-fg disabled:opacity-50 focus:outline-none focus-visible:shadow-focus"
                  >
                    Remove
                  </button>
                </span>
              </div>
            ) : (
              <div className="mt-2.5 flex items-center justify-between gap-2 flex-wrap">
                <button type="button" onClick={() => setConfirmRemove(true)} className={`${LINK_BTN} text-error hover:text-error`}>
                  Remove note
                </button>
                <span className="flex gap-1.5">
                  <Button variant="ghost" size="sm" onClick={cancel} disabled={busy}>
                    Cancel
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => void save()} disabled={busy}>
                    {busy ? 'Saving…' : 'Save'}
                  </Button>
                </span>
              </div>
            )}
            {error && <p role="alert" className="mt-2 text-[12.5px] text-error">{error}</p>}
          </>
        ) : (
          <>
            <NoteBody text={note.body} />
            {otherDevice && (
              <p className="mt-2 text-[11.5px] text-faint">
                Written on another device. To change it, open this page there, or add a new note here.
              </p>
            )}
            {error && <p role="alert" className="mt-2 text-[12.5px] text-error">{error}</p>}
          </>
        )}
      </div>
    </li>
  );
}

// ---------- Admin ----------

function AdminNotes({ scriptId }: AdminProps) {
  const [notes, setNotes] = useState<NoteEntry[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.listScriptNotes(scriptId);
      setNotes(res.notes);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [scriptId]);

  useEffect(() => {
    void load();
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  async function remove(note: NoteEntry) {
    setDeletingId(note.id);
    try {
      await adminApi.deleteScriptNote(scriptId, note.id);
      setNotes((prev) => (prev ?? []).filter((n) => n.id !== note.id));
      setConfirmId(null);
      showToast('success', 'Note deleted.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setNotes((prev) => (prev ?? []).filter((n) => n.id !== note.id));
        setConfirmId(null);
      } else {
        showToast('error', "That note couldn't be deleted. Try again.");
      }
    } finally {
      setDeletingId(null);
    }
  }

  const count = notes?.length ?? 0;

  return (
    <section data-print-hide className="bg-card border border-line rounded-md p-5 md:p-6">
      <div className="flex items-center justify-between gap-2 mb-3">
        <Eyebrow accent>// Client notes</Eyebrow>
        {count > 0 && <Eyebrow>{count} {count === 1 ? 'note' : 'notes'}</Eyebrow>}
      </div>
      {notes === null ? (
        failed ? (
          <p className="text-faint text-[13px]">
            Notes couldn't load.{' '}
            <button type="button" onClick={() => void load()} className={`${LINK_BTN} text-accent hover:text-accent-hover`}>
              Retry
            </button>
          </p>
        ) : (
          <p className="text-faint text-[13px]">Loading…</p>
        )
      ) : notes.length === 0 ? (
        <p className="text-faint text-[13px]">The client hasn't left general notes yet.</p>
      ) : (
        <ul>
          {notes.map((note) => {
            const confirming = confirmId === note.id;
            const firstName = note.author_name.trim().split(/\s+/)[0] || 'this';
            return (
              <li key={note.id} className="border-t border-line first:border-t-0 py-3 first:pt-1">
                <div className="flex items-baseline gap-2">
                  <span className="self-center shrink-0">
                    <Avatar name={note.author_name} legacy={note.legacy} />
                  </span>
                  <div className="min-w-0 flex-1 flex items-baseline gap-x-2 gap-y-0.5 flex-wrap">
                    <b className="min-w-0 break-words text-[12.5px] font-semibold text-fg">
                      {note.legacy ? LEGACY_NOTES_TITLE : note.author_name}
                    </b>
                    {note.legacy ? (
                      <span className="font-mono text-[10px] tracking-[0.02em] text-faint">From before notes had names</span>
                    ) : (
                      <time dateTime={new Date(note.created_at).toISOString()} className="font-mono text-[10px] tracking-[0.02em] text-faint">
                        {formatNoteTime(note.created_at)}
                      </time>
                    )}
                    {note.edited && <span className="font-mono text-[10px] tracking-[0.02em] text-faint">· edited</span>}
                  </div>
                  {!confirming && (
                    <button type="button" onClick={() => setConfirmId(note.id)} className={`${LINK_BTN} shrink-0`}>
                      Delete
                    </button>
                  )}
                </div>
                <NoteBody text={note.body} compact />
                {confirming && (
                  <div className="mt-2.5 flex items-center justify-between gap-2.5 flex-wrap rounded-sm px-3 py-2.5 bg-error/10 border border-error/25 text-[12.5px] text-fg">
                    <span>
                      {note.legacy
                        ? "Delete the earlier notes? The client won't see them again."
                        : `Delete ${firstName}'s note? The client won't see it again.`}
                    </span>
                    <span className="flex gap-1.5">
                      <Button variant="ghost" size="sm" onClick={() => setConfirmId(null)} disabled={deletingId === note.id}>
                        Keep
                      </Button>
                      <button
                        type="button"
                        onClick={() => void remove(note)}
                        disabled={deletingId === note.id}
                        className="inline-flex items-center rounded-full px-3 py-1.5 text-[13px] font-semibold bg-error text-accent-fg disabled:opacity-50 focus:outline-none focus-visible:shadow-focus"
                      >
                        Delete
                      </button>
                    </span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
